/**
 * Traces the tutor's left outline from a rendered frame's alpha channel, so the
 * conversation text can wrap around the avatar that is actually on screen.
 *
 * Coordinates are percentages of the traced box (the avatar's container), which
 * is also the box of the text-wrap float, so a CSS `polygon()` built from them
 * lines up with her. Only the left edge matters: text sits to her left.
 */

export interface SilhouetteBand {
  /** Band centre, as a percentage of the box height. */
  y: number;
  /** Her leftmost opaque pixel in the band, as a percentage of the box width. */
  x: number;
}

export interface Silhouette {
  /** Opaque bounds as percentages of the box: her figure, not the canvas. */
  bounds: { left: number; right: number; top: number; bottom: number };
  /** Left edge from the top of her head down to the bottom of the box. */
  outline: SilhouetteBand[];
  /** Where her shoulders begin (percentage of box height): text may run below it. */
  faceEnd: number;
  /** Her face's leftmost point (percentage of box width), hair included. */
  faceLeft: number;
}

export interface TraceOptions {
  /** Alpha (0-255) above which a pixel counts as her. */
  alphaThreshold?: number;
  /** Number of horizontal bands between her top and the bottom of the box. */
  bands?: number;
}

const round = (value: number) => Math.round(value * 10) / 10;

/**
 * Returns null when the frame holds no opaque pixels (not rendered yet, or
 * rendering paused).
 */
export function traceSilhouette(
  alpha: (x: number, y: number) => number,
  width: number,
  height: number,
  { alphaThreshold = 77, bands = 40 }: TraceOptions = {}
): Silhouette | null {
  if (width <= 0 || height <= 0) return null;

  let left = width;
  let right = -1;
  let top = height;
  let bottom = -1;
  const rowLeft = new Int32Array(height).fill(-1);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (alpha(x, y) <= alphaThreshold) continue;
      if (rowLeft[y] === -1) rowLeft[y] = x;
      if (x < left) left = x;
      if (x > right) right = x;
      if (y < top) top = y;
      if (y > bottom) bottom = y;
    }
  }

  if (right < 0) return null;

  // Bands from her top to the bottom of the box: the figure runs off the
  // bottom edge, and the wrap must reach it.
  const outline: SilhouetteBand[] = [];
  const span = height - top;
  for (let band = 0; band < bands; band += 1) {
    const from = top + Math.floor((span * band) / bands);
    const to = Math.max(from + 1, top + Math.floor((span * (band + 1)) / bands));
    let bandLeft = -1;
    for (let y = from; y < Math.min(to, height); y += 1) {
      const rowX = rowLeft[y];
      if (rowX >= 0 && (bandLeft === -1 || rowX < bandLeft)) bandLeft = rowX;
    }
    if (bandLeft === -1) continue;
    outline.push({ y: round((from / height) * 100), x: round((bandLeft / width) * 100) });
  }

  // Shoulders begin below the neck. Her head's widest point is the smallest
  // left x in the top 40% of her; the neck is the narrowest point (largest left
  // x) below it, within the top 70%. Below the neck, the first band whose edge
  // moves out by more than 2% of the box width is her shoulder.
  const topPercent = (top / height) * 100;
  const spanPercent = (span / height) * 100;
  const upper = outline.filter((band) => band.y < topPercent + spanPercent * 0.7);
  let headIndex = 0;
  upper.forEach((band, index) => {
    if (band.y < topPercent + spanPercent * 0.4 && band.x < upper[headIndex].x) headIndex = index;
  });
  let neckIndex = headIndex;
  for (let index = headIndex + 1; index < upper.length; index += 1) {
    if (upper[index].x >= upper[neckIndex].x) neckIndex = index;
  }
  const neckX = upper[neckIndex]?.x ?? 0;
  let faceEndIndex = outline.length - 1;
  for (let index = neckIndex + 1; index < outline.length; index += 1) {
    if (outline[index].x < neckX - 2) {
      faceEndIndex = index - 1;
      break;
    }
  }
  const faceBands = outline.slice(0, faceEndIndex + 1);
  const faceEnd = outline[faceEndIndex]?.y ?? 100;
  const faceLeft = faceBands.length ? Math.min(...faceBands.map((band) => band.x)) : 0;

  return {
    bounds: {
      left: round((left / width) * 100),
      right: round(((right + 1) / width) * 100),
      top: round((top / height) * 100),
      bottom: round(((bottom + 1) / height) * 100),
    },
    outline,
    faceEnd,
    faceLeft,
  };
}

/** Her whole left outline as a CSS polygon over the box (closed along the right edge). */
export function outlinePolygon(silhouette: Silhouette): string {
  const { outline, bounds } = silhouette;
  const points = [
    `100% ${bounds.top}%`,
    ...outline.map((band) => `${band.x}% ${band.y}%`),
    `${outline[outline.length - 1]?.x ?? 0}% 100%`,
    "100% 100%",
  ];
  return `polygon(${points.join(", ")})`;
}

/**
 * Her head and neck only, re-expressed in a box that spans from her top to
 * where her shoulders begin: the part that text must never cover.
 */
export function facePolygon(silhouette: Silhouette): string {
  const { outline, bounds, faceEnd } = silhouette;
  const spanPercent = Math.max(1, faceEnd - bounds.top);
  const toBand = (y: number) => round(((y - bounds.top) / spanPercent) * 100);
  const head = outline.filter((band) => band.y <= faceEnd);
  const points = [
    "100% 0%",
    ...head.map((band) => `${band.x}% ${toBand(band.y)}%`),
    `${head[head.length - 1]?.x ?? 0}% 100%`,
    "100% 100%",
  ];
  return `polygon(${points.join(", ")})`;
}
